import styled, { css, DefaultTheme } from 'styled-components'

import { ButtonProps } from '@Components/Button/index'
import { up } from '@Globals/Style/breakpoints'

type StyleProps = ButtonProps & { theme: DefaultTheme }

const whiteButton = ({
  color,
  disabled,
  theme,
  variant,
}: StyleProps) => {
  if (color !== 'white') {
    return ''
  }

  if (variant === 'full') {
    return css`
      & > span {
        color: ${disabled ? theme.colors.grey : theme.colors.greyDark};
      }

      ${disabled && css`
        background-color: ${theme.colors.greyLighter};
        border-color: ${theme.colors.greyLighter};
      `}
      ${!disabled && css`
        &:hover {
          background-color: ${theme.colors.whiteDarkened5};
          border-color: ${theme.colors.whiteDarkened5};
        }
      `}
    `
  }

  if (variant === 'outlined') {
    return css`
      & > span {
        color: ${disabled ? theme.colors.grey : theme.colors.greyDark};
      }

      ${!disabled && css`
        &:hover {
          background-color: ${theme.colors.whiteDarkened5};
          border-color: ${theme.colors.whiteDarkened5};
        }
      `}
    `
  }

  return ''
}

const primaryButton = ({
  color,
  disabled,
  theme,
  variant,
}: StyleProps) => {
  if (color === 'primary' && variant === 'full') {
    if (disabled) {
      return css`
        background-color: ${theme.colors.grey};
        border-color: ${theme.colors.grey};
        color: white;
      `
    }

    return css`
      background-color: ${theme.colors.primary};
      border-color: ${theme.colors.primary};
      color: white;

      span {
        color: white;
      }

      &:hover {
        background-color: ${theme.colors.primaryHover};
        border-color: ${theme.colors.primaryHover};
      }
    `
  }
  if (color === 'primary' && variant === 'outlined') {
    if (disabled) {
      return css`
        background-color: ${theme.colors.white};
        border-color: ${theme.colors.grey};

        & > span {
          color: ${theme.colors.grey};
        }
      `
    }

    return css`
      background-color: ${theme.colors.white};
      border-color: ${theme.colors.primary};

      &:hover {
        border-color: ${theme.colors.primaryHover};

        & > span {
          color: ${theme.colors.primaryHover};
        }
      }

      & > span {
        color: ${theme.colors.primary};
      }
    `
  }
  return ''
}

const darkButton = ({
  color,
  disabled,
  theme,
  variant,
}: StyleProps) => {
  if (color === 'dark' && variant === 'full') {
    const baseStyling = css`
      span {
        color: ${theme.colors.white};
      }
    `

    if (disabled) {
      return css`
        ${baseStyling};
        background-color: ${theme.colors.grey};
        border-color: ${theme.colors.grey};
      `
    }

    return css`
      ${baseStyling};
      background-color: ${theme.colors.greyDark};
      border-color: ${theme.colors.greyDark};

      &:hover {
        background-color: ${theme.colors.greyDarkLightened10};
        border-color: ${theme.colors.greyDarkLightened10};
      }
    `
  }
  if ((color === 'dark' || color === 'grey') && variant === 'outlined') {
    return css`
      background-color: #fff;
      border-color: ${theme.colors.greyLight};

      & > span {
        color: ${disabled ? theme.colors.greyLight : theme.colors.greyDark};
      }

      ${!disabled && `
        &:hover {
          background-color: ${theme.colors.greyLightest};
        }
      `}
    `
  }
  return ''
}

const greyButton = ({
  color,
  disabled = false,
  theme,
  variant,
}: StyleProps) => {
  if (color === 'grey' && variant === 'full') {
    return css`
      background-color: ${theme.colors.greyLightest};
      border-color: #e5e5e5;

      ${!disabled && `
        &:hover {
          background-color: #e9e9e9;
          border-color: ${theme.colors.greyLight};
        }
      `}
      span {
        color: ${disabled ? theme.colors.greyLight : theme.colors.greyDark};
      }
    `
  }

  return ''
}

const ButtonSpan = styled.span<ButtonProps>`
  ${({ secondaryContent }) => (!!secondaryContent && css`
    color: inherit;
  `)};
`

export const ButtonAdditionalContent = styled(ButtonSpan)<ButtonProps>`
  margin-left: .8em;
  line-height: 1;
`

export const ButtonText = styled(ButtonSpan)<ButtonProps>`
  color: #fff;
  display: block;
  pointer-events: none;
  text-transform: uppercase;
  
`

export const ButtonRoot = styled.button<ButtonProps>`
  display: ${({ width }) => (width === 'auto' ? 'inline-flex' : 'flex')};
  padding: ${({ size }) => (size === 'medium' ? '11px 20px 10px' : '4px 5px 5px')};
  max-width: 500px;
  align-items: center;
  justify-content: center;

  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  background-color: #fff;
  border: 1px solid #fff;
  border-radius: ${({ theme: { borderRadius } }) => borderRadius};
  transition: transform 0.075s ease;

  ${up('phablet')} {
    transition: transform 0.1s ease;
    max-width: unset;
  }

  ${({ disabled }) => (!disabled && css`
    &:active {
      transform: scale(0.96);
    }
  `)};

  ${({ width }) => (width === 'full' && css`
    margin-bottom: 10px;
    width: 100%
  `)};

  ${({ secondaryContent }) => (!!secondaryContent && css`
    justify-content: space-between;
    align-items: center;

    svg {
      width: 1em;
      line-height: 1;
    }

    svg, path {
      fill: currentColor;
      color: inherit;
    }
  `)};

  ${ButtonText} {
    ${({ size }) => size === 'small' && css`
      font-size: 0.75rem;
    `}
  }

  ${whiteButton}
  ${primaryButton}
  ${darkButton}
  ${greyButton}
`
